import React, { useContext, useState } from 'react';
import classNames from 'classnames';
import { AccountButton } from '../account-button/AccountButton';
import { AccountMenu } from '../account-menu/AccountMenu';
import { AccountWishList } from '../account-wishlist/AccountWishList';
import { formatGreeting } from '../../utils/user/format-greeting';
import {
  isMin1280pxWide,
  isTouchDevice,
} from '../../utils/device/identifyDevice';
import { UserContext } from '../../scaffold/UserContext';
import { getAccountLinks } from '../links';
import { notifyNavAnalytics } from '../navigation/notify-analytics';

import styles from './account-links.scss';
import { EnvVarsFeaturesContext } from '../../scaffold/EnvVarsFeaturesContext';

export const AccountLinks = ({
  lgOnlyClassname,
}: {
  lgOnlyClassname?: string;
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const {
    envVars: { accountBaseUrl, myAccountUrl },
    features,
  } = useContext(EnvVarsFeaturesContext);
  const {
    userState: {
      user: { signedIn, greeting },
    },
  } = useContext(UserContext);

  const shouldHideAccount = features.mvoMode || features.hideAccountIcon;
  const shouldHideWishList = features.mvoMode || features.hideWishListIcon;

  const toggleMenu = (
    e: React.MouseEvent<HTMLDivElement | HTMLLIElement | HTMLAnchorElement>
  ) => {
    e.preventDefault();
    setIsMenuOpen(!isMenuOpen);
  };

  const handleOnClickSignedOut = () => {
    if (!isMin1280pxWide()) {
      notifyNavAnalytics('mv', 'Account');
    }
  };

  const handleOnClickSignedIn = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!isMin1280pxWide()) {
      notifyNavAnalytics('mv', 'Account');
    }
    if (isTouchDevice() && isMin1280pxWide()) {
      toggleMenu(e);
    }
  };

  const handleMouseToggle = (e: React.MouseEvent<HTMLLIElement>) => {
    if (!isTouchDevice()) {
      toggleMenu(e);
    }
  };

  const formattedGreeting = formatGreeting(greeting);
  const displayedGreeting =
    formattedGreeting === '' ? 'My account' : `Hello ${formattedGreeting}`;
  const accountLinks = getAccountLinks(accountBaseUrl, myAccountUrl);

  const wishListUrl = !signedIn
    ? `/wish-list/guest`
    : `${accountBaseUrl}/wish-list`;

  const signInUrl = `/sign-in`;
  const registerUrl = `/register`;

  return (
    <>
      {!shouldHideAccount && (
        <>
          {signedIn ? (
            <li
              className={styles.accountButton}
              data-test="menu-triggers"
              data-testid="menu-trigger"
              onMouseEnter={handleMouseToggle}
              onMouseLeave={handleMouseToggle}
            >
              <div
                className={styles.account}
                data-testid="account-button-container"
              >
                <AccountButton
                  onClick={handleOnClickSignedIn}
                  greeting={displayedGreeting}
                  myAccountUrl={myAccountUrl}
                />
                <AccountMenu isOpen={isMenuOpen} accountLinks={accountLinks} />
              </div>
            </li>
          ) : (
            <>
              <li
                className={classNames(styles.signIn, lgOnlyClassname)}
                data-testid="account-sign-in-url"
              >
                <a href="/sign-in">Sign in</a>
              </li>
              <li className={styles.accountButton}>
                <AccountButton
                  onClick={handleOnClickSignedOut}
                  myAccountUrl={myAccountUrl}
                />
              </li>
            </>
          )}
        </>
      )}
      {!shouldHideWishList && (
        <AccountWishList
          wishListUrl={wishListUrl}
          signInUrl={signInUrl}
          registerUrl={registerUrl}
          signedIn={signedIn}
          lgOnlyClassname={lgOnlyClassname}
        />
      )}
    </>
  );
};
